// src/layouts/Sidebar.js
import React, { Component } from "react";
import { Link, NavLink } from "react-router-dom";
import PerfectScrollbar from "react-perfect-scrollbar";
import { Spinner } from "react-bootstrap";
import '../assets/css/sidebar.css';
import { getDashboardMenu, ROLES } from "../data/Menu";
import { COLORS } from "../constants/colors";

// Composant pour le footer de la sidebar
const SidebarFooter = ({ user, toggleFooterMenu, handleLogout }) => {
    const getUserInitials = () => {
        if (!user) return 'U';
        const prenom = user.prenom || user.prenom_utilisateur || '';
        const nom = user.nom || user.nom_utilisateur || '';
        return (prenom.charAt(0) + nom.charAt(0)).toUpperCase() || 'U';
    };

    const getUserFullName = () => {
        if (!user) return 'Invité';
        const prenom = user.prenom || user.prenom_utilisateur || '';
        const nom = user.nom || user.nom_utilisateur || '';
        return `${prenom} ${nom}`.trim() || 'Utilisateur';
    };

    const getRoleBadgeColor = () => {
        const colors = {
            [ROLES.SUPER_ADMIN]: 'danger',
            [ROLES.ADMIN]: 'danger',
            [ROLES.PASTEUR]: 'primary',
            [ROLES.ANCIEN]: 'info',
            [ROLES.DIACRE]: 'success',
            [ROLES.DIACONESSE]: 'success',
            [ROLES.DP]: 'secondary',
            [ROLES.ACCUEIL]: 'warning',
            [ROLES.NOUVEAU]: 'light'
        };
        return colors[user?.role] || 'secondary';
    };

    return (
        <div className="sidebar-footer">
            <div className="sidebar-footer-top">
                <div className="sidebar-footer-thumb">
                    {user?.avatar ? (
                        <img 
                            src={`https://josephsbusiness.com/assets/images/${user.avatar}`} 
                            alt="Avatar"
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = "../assets/img/default-avatar.jpg";
                            }}
                        />
                    ) : (
                        <div style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            backgroundColor: COLORS.primary,
                            color: COLORS.white,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '16px',
                            fontWeight: 'bold'
                        }}>
                            {getUserInitials()}
                        </div>
                    )}
                </div>
                <div className="sidebar-footer-body" style={{ minWidth: 0, overflow: 'hidden' }}>
                    <h6 style={{ 
                        fontSize: '14px',
                        margin: 0,
                        lineHeight: 1.8,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                    }}>
                        <Link to="/fidele" style={{ color: COLORS.white }}>
                            {getUserFullName()}
                        </Link>
                    </h6>
                    <p style={{ margin: 0, fontSize: '11px', lineHeight: 1.2 }}>
                        <span className={`badge bg-${getRoleBadgeColor()}`} style={{
                            fontSize: '10px',
                            padding: '3px 6px',
                            display: 'inline-block',
                            maxWidth: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}>
                            {user?.role || 'Visiteur'}
                        </span>
                    </p>
                </div>
                <Link to="#" className="dropdown-link" onClick={toggleFooterMenu}>
                    <i className="ri-arrow-down-s-line"></i>
                </Link>
            </div>
            
            <div className="sidebar-footer-menu">
                <nav className="nav">
                    <Link to="/fidele">
                        <i className="ri-profile-line"></i> Mon profil
                    </Link>
                    <Link to={`/profil/modifier/${user?.id}`}>
                        <i className="ri-edit-2-line"></i> Modifier profil
                    </Link>
                </nav>
                <hr />
                <nav className="nav">
                    <Link to="/aide">
                        <i className="ri-question-line"></i> Centre d'aide
                    </Link>
                    <Link to="/parametres">
                        <i className="ri-settings-3-line"></i> Paramètres
                    </Link>
                    <Link to="/logout" onClick={handleLogout}>
                        <i className="ri-logout-box-r-line"></i> Déconnexion
                    </Link>
                </nav>
            </div>
        </div>
    );
};

export default class Sidebar extends Component {
    constructor(props) {
        super(props);
        this.state = {
            user: null,
            loading: true
        };
        this._scrollBarRef = React.createRef();
    }

    componentDidMount() {
        this.loadUser();
        window.addEventListener('userUpdated', this.handleUserUpdate);
        window.addEventListener('storage', this.handleStorageChange);
    }

    componentWillUnmount() {
        window.removeEventListener('userUpdated', this.handleUserUpdate);
        window.removeEventListener('storage', this.handleStorageChange);
    }

    handleUserUpdate = () => {
        this.loadUser();
    }

    handleStorageChange = (e) => {
        if (e.key === 'user') {
            this.loadUser();
        }
    }

    loadUser = () => {
        try {
            const userStr = localStorage.getItem("user");
            if (userStr && userStr !== "undefined" && userStr !== "null") {
                const userData = JSON.parse(userStr);
                this.setState({ user: userData, loading: false });
            } else {
                this.setState({ loading: false });
            }
        } catch (error) {
            console.error("❌ Sidebar - Erreur:", error);
            this.setState({ loading: false });
        }
    }

    toggleFooterMenu = (e) => {
        e.preventDefault();
        let parent = e.target.closest(".sidebar");
        if (parent) {
            parent.classList.toggle("footer-menu-show");
            if (this._scrollBarRef.current) {
                setTimeout(() => this._scrollBarRef.current.updateScroll(), 300);
            }
        }
    }

    handleLogout = (e) => {
        e.preventDefault();
        localStorage.clear();
        window.location.href = '/login';
    }

    updateScrollbar = () => {
        if (this._scrollBarRef.current) {
            this._scrollBarRef.current.updateScroll();
        }
    }

    render() {
        const { user, loading } = this.state;

        // Injection des variables CSS dynamiques à partir de COLORS
        const styleVariables = `
            :root {
                --primary-color: ${COLORS.primary};
                --primary-dark: ${COLORS.primaryDark};
                --secondary-color: ${COLORS.secondary};
                --secondary-dark: ${COLORS.secondaryDark};
                --primary-light: rgba(255,255,255,0.12);
                --primary-soft: rgba(255,255,255,0.06);
                --secondary-light: rgba(253, 181, 47, 0.3);
                --white: ${COLORS.white};
                --light-gray: ${COLORS.lightGray};
                --dark: ${COLORS.dark};
                --text-gray: ${COLORS.textGray};
                --gradient-primary: linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark});
                --gradient-primary-secondary: linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary});
                --primary-shadow: 0 4px 12px rgba(41, 90, 167, 0.4);
                --secondary-shadow: 0 4px 12px rgba(253, 181, 47, 0.4);
            }
        `;

        if (loading) {
            return (
                <div className="sidebar d-flex align-items-center justify-content-center">
                    <Spinner animation="border" variant="light" />
                </div>
            );
        }

        const truncateText = (text, maxLength = 27) => {
            if (!text) return '';
            return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
        };

        const dashboardMenu = getDashboardMenu(user);

        return (
            <>
                {/* Injection des variables CSS */}
                <style>{styleVariables}</style>

                <div className="sidebar">
                    <div className="sidebar-header d-flex align-items-center justify-content-center">
                        <Link to="/accueil" className="sidebar-logo">
                            <div className="logo-container">
                                <img 
                                    src={user?.logo_parent 
                                        ? `https://josephsbusiness.com/assets/images/${user.logo_parent}` 
                                        : "../assets/img/logo.jpeg"
                                    } 
                                    alt="Logo" 
                                    style={{ height: '50px', width: '50px', borderRadius: '50%' }}
                                    onError={(e) => { e.target.src = "../assets/img/logo.jpeg"; }}
                                />
                            </div>
                        </Link>
                    </div>
                    
                    <div className="text-center p-2 mb-2">
                        <span className="badge" style={{ 
                            fontSize: '12px',
                            backgroundColor: COLORS.secondary,
                            color: 'white !important'
                        }}>
                            {truncateText(user?.temple || 'JOSEPHSBUSINESS')}
                        </span>
                    </div>
                    
                    <PerfectScrollbar 
                        className="sidebar-body" 
                        ref={this._scrollBarRef}
                        options={{ suppressScrollX: true }}
                    >
                        <SidebarMenu 
                            menuItems={dashboardMenu}
                            onUpdateSize={this.updateScrollbar}
                        />
                        <><br></br><br></br><br></br><br></br></>
                    </PerfectScrollbar>
                     
                    <SidebarFooter 
                        user={user}
                        toggleFooterMenu={this.toggleFooterMenu}
                        handleLogout={this.handleLogout}
                    />
                </div>
            </>
        );
    }
}

class SidebarMenu extends Component {
    toggleSubMenu = (e) => {
        e.preventDefault();
        let parent = e.target.closest('.nav-item');
        if (!parent) return;
        
        let node = parent.parentNode?.firstChild;
        while (node) {
            if (node !== parent && node.nodeType === Node.ELEMENT_NODE)
                node.classList.remove('show');
            node = node.nextElementSibling || node.nextSibling;
        }
        parent.classList.toggle('show');
        this.props.onUpdateSize();
    }

    toggleMenu = (e) => {
        e.preventDefault();
        let parent = e.target.closest('.nav-group');
        if (parent) {
            parent.classList.toggle('show');
            this.props.onUpdateSize();
        }
    }

    populateMenu = (menuItems) => {
        if (!menuItems || !Array.isArray(menuItems)) return null;

        const menu = menuItems.map((item, key) => {
            let submenuItems;
            if (item.submenu && Array.isArray(item.submenu)) {
                submenuItems = item.submenu.map((subItem, subKey) => (
                    <NavLink 
                        to={subItem.link} 
                        className="nav-sub-link text-white" 
                        key={subKey}
                        onClick={this.props.onUpdateSize}
                    >
                        {subItem.label}
                    </NavLink>
                ));
            }

            return (
                <li key={key} className="nav-item">
                    {!submenuItems ? (
                        <NavLink to={item.link} className="nav-link" onClick={this.props.onUpdateSize}>
                            <i className={item.icon}></i> 
                            <span>{item.label}</span>
                        </NavLink>
                    ) : (
                        <div onClick={this.toggleSubMenu} className="nav-link has-sub">
                            <i className={item.icon}></i> 
                            <span>{item.label}</span>
                        </div>
                    )}
                    {item.submenu && <nav className="nav nav-sub">{submenuItems}</nav>}
                </li>
            );
        });

        return <ul className="nav nav-sidebar">{menu}</ul>;
    }

    render() {
        const { menuItems } = this.props;

        if (!menuItems || menuItems.length === 0) {
            return (
                <div className="p-3 text-center">
                    <i className="ri-inbox-line fs-1 text-muted mb-2"></i>
                    <p className="text-muted small">Aucun menu disponible</p>
                </div>
            );
        }

        return (
            <>
            <div className="nav-group show">
                <div className="nav-label" onClick={this.toggleMenu}>
                    <i className="ri-menu-line" style={{ marginRight: '5px' }}></i>
                    <span className="text-white">MENU PRINCIPAL</span>
                </div>
                {this.populateMenu(menuItems)}
            </div>
           
            </>
        );
    }
}

// Event listeners
window.addEventListener("click", function (e) {
    let sidebar = document.querySelector(".sidebar");
    if (!e.target.closest(".sidebar-footer") && sidebar) {
        sidebar.classList.remove("footer-menu-show");
    }
    if (!e.target.closest(".sidebar") && !e.target.closest(".menu-link")) {
        document.body.classList.remove("sidebar-show");
    }
});

window.addEventListener("load", function () {
    let skinMode = localStorage.getItem("sidebar-skin");
    if (skinMode) {
        document.querySelector("html").setAttribute("data-sidebar", skinMode);
    }
});